// frontend/src/pages/NotificationsPage.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Bell, MessageSquare, BookOpen, Star, Trash2,
  CheckCheck, Film, Loader, Heart
} from 'lucide-react';
import {
  getNotifications,
  markNotificationsRead,
  getLocalNotifications,
  markAllLocalNotificationsRead,
  markLocalNotificationRead,
  deleteLocalNotification,
  LocalNotification,
} from '../utils/notifications';
import { useAppContext } from '../context/AppContext';

// ─────────────────────────────────────────────
// ICON MAP for local notification types
// ─────────────────────────────────────────────
const typeIcon: Record<string, React.ReactNode> = {
  review_posted: <Star className="w-4 h-4 text-yellow-500" />,
  review_updated: <Star className="w-4 h-4 text-blue-400" />,
  comment_posted: <MessageSquare className="w-4 h-4 text-romcom-blue" />,
  notebook_logged: <BookOpen className="w-4 h-4 text-romcom-red" />,
};

const typeBg: Record<string, string> = {
  review_posted: 'bg-yellow-50 border-yellow-100',
  review_updated: 'bg-blue-50 border-blue-100',
  comment_posted: 'bg-blue-50 border-blue-100',
  notebook_logged: 'bg-romcom-pink/10 border-romcom-pink/20',
};

// ─────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────
const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { state, openLoginModal } = useAppContext();

  const [serverNotifs, setServerNotifs] = useState<any[]>([]);
  const [localNotifs, setLocalNotifs] = useState<LocalNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'activity' | 'social'>('activity');

  const refreshLocal = useCallback(() => {
    setLocalNotifs(getLocalNotifications());
  }, []);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      refreshLocal();
      try {
        const data = await getNotifications();
        setServerNotifs(data);
      } catch {
        setServerNotifs([]);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [refreshLocal]);

  const handleMarkAllRead = async () => {
    markAllLocalNotificationsRead();
    refreshLocal();
    await markNotificationsRead();
    setServerNotifs(prev => prev.map(n => ({ ...n, isRead: true })));
  };

  const handleDeleteLocal = (id: string) => {
    deleteLocalNotification(id);
    refreshLocal();
  };

  const handleReadLocal = (id: string) => {
    markLocalNotificationRead(id);
    refreshLocal();
  };

  const unreadLocal = localNotifs.filter(n => !n.isRead).length;
  const unreadServer = serverNotifs.filter(n => !n.isRead).length;
  const totalUnread = unreadLocal + unreadServer;

  if (!state.currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-romcom-pink/10 to-romcom-blue/10">
        <div className="text-center">
          <Bell className="w-16 h-16 text-gray-200 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-700 mb-2">Sign in to see notifications</h2>
          <button onClick={openLoginModal} className="btn-primary mt-4">Sign In</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-romcom-pink/10 to-romcom-blue/10">
      <div className="container mx-auto px-4 pt-24 pb-16 max-w-2xl">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <Bell className="w-7 h-7 text-gray-800" />
              {totalUnread > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-romcom-red text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                  {totalUnread > 9 ? '9+' : totalUnread}
                </span>
              )}
            </div>
            <h1 className="text-3xl font-playfair font-bold text-gray-800">Notifications</h1>
          </div>
          {totalUnread > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="flex items-center gap-2 text-sm text-romcom-red hover:underline"
            >
              <CheckCheck className="w-4 h-4" />
              Mark all read
            </button>
          )}
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { key: 'activity', label: 'My Activity', count: unreadLocal },
            { key: 'social', label: 'Social', count: unreadServer },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-sm transition-all ${
                activeTab === tab.key
                  ? 'bg-romcom-red text-white shadow-md'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                  activeTab === tab.key ? 'bg-white/20 text-white' : 'bg-romcom-red/10 text-romcom-red'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader className="w-8 h-8 text-romcom-red animate-spin" />
          </div>
        ) : (
          <AnimatePresence mode="wait">

            {/* ── MY ACTIVITY TAB (local notifications) ── */}
            {activeTab === 'activity' && (
              <motion.div
                key="activity"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                {localNotifs.length === 0 ? (
                  <div className="text-center py-16 bg-[#faf8f6] rounded-2xl border border-romcom-pink/20">
                    <Heart className="w-12 h-12 text-romcom-pink/20 mx-auto mb-4" />
                    <p className="text-gray-500 font-medium">No activity yet</p>
                    <p className="text-gray-400 text-sm mt-1">Write a review or log a movie to get started</p>
                    <button onClick={() => navigate('/movies')} className="btn-primary mt-6">
                      Browse Movies
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {localNotifs.map((notif, i) => (
                      <motion.div
                        key={notif.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.04 }}
                        className={`relative flex items-start gap-4 p-4 rounded-2xl border transition-all cursor-pointer ${
                          !notif.isRead
                            ? typeBg[notif.type] || 'bg-romcom-pink/5 border-romcom-pink/10'
                            : 'bg-white border-gray-100'
                        }`}
                        onClick={() => {
                          handleReadLocal(notif.id);
                          if (notif.movieId) navigate(`/movies/${notif.movieId}`);
                        }}
                      >
                        {/* Unread dot */}
                        {!notif.isRead && (
                          <span className="absolute top-4 right-10 w-2 h-2 bg-romcom-red rounded-full" />
                        )}

                        {/* Icon */}
                        <div className="w-9 h-9 rounded-full bg-white shadow-sm border border-gray-100 flex items-center justify-center flex-shrink-0">
                          {typeIcon[notif.type] || <Bell className="w-4 h-4 text-gray-400" />}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm leading-relaxed ${!notif.isRead ? 'font-medium text-gray-800' : 'text-gray-600'}`}>
                            {notif.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(notif.createdAt).toLocaleString('en-US', {
                              month: 'short', day: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </p>
                          {notif.movieTitle && (
                            <span className="inline-flex items-center gap-1 mt-1.5 text-xs text-romcom-red/80 bg-romcom-pink/10 px-2 py-0.5 rounded-full">
                              <Film className="w-3 h-3" />
                              {notif.movieTitle}
                            </span>
                          )}
                        </div>

                        {/* Delete */}
                        <button
                          onClick={e => { e.stopPropagation(); handleDeleteLocal(notif.id); }}
                          className="p-1.5 text-gray-300 hover:text-red-400 transition-colors flex-shrink-0"
                          title="Remove"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* ── SOCIAL TAB (server notifications) ── */}
            {activeTab === 'social' && (
              <motion.div
                key="social"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                {serverNotifs.length === 0 ? (
                  <div className="text-center py-16 bg-[#faf8f6] rounded-2xl border border-romcom-pink/20">
                    <Bell className="w-12 h-12 text-romcom-pink/20 mx-auto mb-4" />
                    <p className="text-gray-500 font-medium">No social notifications</p>
                    <p className="text-gray-400 text-sm mt-1">When someone likes or comments on your reviews, you'll see it here</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {serverNotifs.map((n, i) => (
                      <motion.div
                        key={n.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.04 }}
                        className={`flex items-start gap-4 p-4 rounded-2xl border transition-all ${
                          !n.isRead ? 'bg-blue-50 border-blue-100' : 'bg-[#faf8f6] border-romcom-pink/20'
                        }`}
                      >
                        {/* Sender avatar */}
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-romcom-pink to-romcom-red flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                          {n.sender?.username?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm leading-relaxed ${!n.isRead ? 'font-medium text-gray-800' : 'text-gray-600'}`}>
                            <span className="font-bold">{n.sender?.username}</span>{' '}
                            {n.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(n.createdAt).toLocaleString('en-US', {
                              month: 'short', day: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </p>
                        </div>
                        {!n.isRead && (
                          <span className="w-2 h-2 bg-romcom-red rounded-full flex-shrink-0 mt-2" />
                        )}
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;